import { createContext, useState, useEffect, useMemo, ReactNode, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { db } from '@/services/firebase/config';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { ClinicService } from '@/services/firebase/clinic';
import { TemplateService } from '@/services/firebase/templates';
import type { Unit } from '@/types/models';
import { orderUnits } from '@/utils/salas';
import { accessibleUnitIds, hasAllUnitsAccess } from '@/utils/unitAccess';

interface ClinicContextType {
  clinicId: string | null;
  currentUnitId: string | null;
  /** Todas as unidades ATIVAS da clínica. É a lista certa pra regra
   *  (de que unidade é o agendamento, médico em dois lugares…); pra
   *  oferecer unidade numa tela, use `accessibleUnits`. */
  units: Unit[];
  /** As unidades que esta conta acessa, na ordem das telas (spec 019 —
   *  autorização de admin/dono, `utils/unitAccess`). `currentUnitId` é
   *  sempre uma delas. */
  accessibleUnits: Unit[];
  /** Acessa todas (admin, dono ou autorizado "todas"): vê também "Todas as
   *  unidades" na Agenda e na Liberar agenda. */
  allUnitsAccess: boolean;
  /**
   * True quando o usuário logado é o `ownerId` do doc clinics/{clinicId}.
   * O owner tem acesso irrestrito ao app, mesmo que o próprio cargo
   * (Role) seja restritivo (ex: AGENDADOR_EXTERNO em modo de teste).
   * Evita lock-out do dono em qualquer cenário.
   */
  isOwner: boolean;
  setClinicId: (id: string) => void;
  setCurrentUnitId: (id: string) => void;
  /**
   * Agenda em "Todas as unidades" (spec 019, fase 2) — por computador, como
   * a unidade atual. Separado de `currentUnitId` de propósito: o formulário
   * e a Liberar agenda continuam precisando de UMA unidade. Só vale pra
   * quem tem `allUnitsAccess`.
   */
  allUnitsView: boolean;
  setAllUnitsView: (v: boolean) => void;
}

/** Unidade atual gravada por computador (spec 019). */
const UNIT_STORAGE_KEY = 'dratento.unidade';
/** Agenda em "Todas as unidades", por computador (spec 019, fase 2). */
const ALL_UNITS_STORAGE_KEY = 'dratento.unidade.todas';
/** A Sede — a unidade que a clínica nasce tendo (`utils/internalUnit.ts`). */
const SEDE_ID = 'unit-1';

export const ClinicContext = createContext<ClinicContextType>({
  clinicId: null,
  currentUnitId: null,
  units: [],
  accessibleUnits: [],
  allUnitsAccess: false,
  isOwner: false,
  setClinicId: () => {},
  setCurrentUnitId: () => {},
  allUnitsView: false,
  setAllUnitsView: () => {},
});

export function useClinic() {
  return useContext(ClinicContext);
}

export function ClinicProvider({ children }: { children: ReactNode }) {
  const { profile } = useContext(AuthContext);
  const [clinicId, setClinicId] = useState<string | null>(null);
  const [currentUnitId, setCurrentUnitIdRaw] = useState<string | null>(null);
  /** Unidade atual POR COMPUTADOR (spec 019, fase 2): a recepção da
   *  Conceição deixa a máquina dela na Conceição. Trocar no Topbar (ou na
   *  Liberar agenda) grava aqui. */
  const setCurrentUnitId = (id: string) => {
    setCurrentUnitIdRaw(id);
    try { localStorage.setItem(UNIT_STORAGE_KEY, id); } catch { /* sem storage: vale só nesta aba */ }
  };
  const [allUnitsView, setAllUnitsViewRaw] = useState<boolean>(() => {
    try { return localStorage.getItem(ALL_UNITS_STORAGE_KEY) === '1'; } catch { return false; }
  });
  const setAllUnitsView = (v: boolean) => {
    setAllUnitsViewRaw(v);
    try { localStorage.setItem(ALL_UNITS_STORAGE_KEY, v ? '1' : '0'); } catch { /* sem storage: vale só nesta aba */ }
  };
  const [units, setUnits] = useState<Unit[]>([]);
  const [isOwner, setIsOwner] = useState<boolean>(false);
  /** Já se sabe se é o dono? Até lá o acesso às unidades é provisório. */
  const [ownerResolved, setOwnerResolved] = useState(false);

  // Sync clinicId from auth profile whenever it changes
  useEffect(() => {
    if (profile?.clinicId) {
      setClinicId(profile.clinicId);
    }
  }, [profile?.clinicId]);

  // Migration idempotente: atualiza o template seed de "Declaração de
  // acompanhante" pra usar placeholders {{acompanhante}}/{{acompanhante_cpf}}
  // se ainda estiver com o conteúdo padrão antigo. Não bloqueia o bootstrap.
  useEffect(() => {
    if (!clinicId) return;
    TemplateService.upgradeAccompanistTemplate(clinicId).catch((err) => {
      console.debug('upgradeAccompanistTemplate fallback:', err?.code ?? err);
    });
    // Backfill: planta os 4 modelos de laudo com tokens de exame em clínicas
    // que já existiam antes da feature (o seed normal só roda com 0 templates).
    TemplateService.seedExamLaudosIfMissing(clinicId).catch((err) => {
      console.debug('seedExamLaudosIfMissing fallback:', err?.code ?? err);
    });
  }, [clinicId]);

  // Fetch clinic doc once to determine ownership. Read-only — no listener
  // needed because ownerId basically never changes.
  useEffect(() => {
    setOwnerResolved(false);
    if (!clinicId || !profile?.uid) {
      setIsOwner(false);
      return;
    }
    let cancelled = false;
    ClinicService.getClinicDoc(clinicId)
      .then((raw) => {
        if (cancelled) return;
        const data = raw as { ownerId?: string } | null;
        setIsOwner(!!data && data.ownerId === profile.uid);
        setOwnerResolved(true);
      })
      .catch((err) => {
        console.debug('Clinic owner check fallback:', err.code);
        if (!cancelled) { setIsOwner(false); setOwnerResolved(true); }
      });
    return () => {
      cancelled = true;
    };
  }, [clinicId, profile?.uid]);

  // Load units for the clinic
  //
  // Uma assinatura só por clínica. Antes ela dependia de `currentUnitId` e
  // `profile.unitIds`, então se refazia logo no boot (a 1ª resposta, do
  // cache, já escolhia a unidade) — e no ar a assinatura refeita ficava
  // presa nessa resposta do cache, com só a Sede (que o AuthContext acabou
  // de ler): as outras unidades "sumiam" de Configurações › Unidades, do
  // Topbar e da Agenda (20/10/2026).
  const [unitsFromServer, setUnitsFromServer] = useState(false);
  useEffect(() => {
    if (!clinicId) {
      setUnits([]);
      setUnitsFromServer(false);
      return;
    }

    const q = query(
      collection(db, `clinics/${clinicId}/units`),
      where('isActive', '==', true),
    );

    // includeMetadataChanges: avisa quando a lista deixa de ser do cache
    // mesmo sem mudar nenhuma unidade (a escolha inicial espera isso).
    const unsubscribe = onSnapshot(q, { includeMetadataChanges: true }, (snapshot) => {
      setUnits(snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Unit[]);
      setUnitsFromServer(!snapshot.metadata.fromCache);
    }, (err) => {
      // Units collection may not exist yet, user may lack permissions, or auth
      // token may not have refreshed claims yet. Fall back to empty list silently.
      // Use console.debug so it's filtered out of the default console view.
      console.debug('Units listener fallback (empty):', err.code);
      setUnits([]);
    });

    return () => unsubscribe();
  }, [clinicId]);

  // Unidades que esta conta acessa (spec 019): admin e dono, todas; os
  // outros, o que admin/dono autorizou — sem autorização, onde atendem.
  const allUnitsAccess = hasAllUnitsAccess(profile?.role, isOwner, profile?.unitAccess);
  const orderedUnits = useMemo(() => orderUnits(units), [units]);
  const accessibleIds = useMemo(() => accessibleUnitIds({
    activeUnitIds: orderedUnits.map((u) => u.id),
    grant: profile?.unitAccess,
    attendUnitIds: profile?.unitIds ?? [],
    allAccess: allUnitsAccess,
  }), [orderedUnits, profile?.unitAccess, profile?.unitIds, allUnitsAccess]);
  const accessibleUnits = useMemo(
    () => orderedUnits.filter((u) => accessibleIds.includes(u.id)),
    [orderedUnits, accessibleIds],
  );
  /** Lista do servidor + perfil + dono conferido: o acesso é o de verdade. */
  const accessReady = unitsFromServer && ownerResolved && !!profile;

  // Unidade atual (spec 019, fase 2): a que ESTE computador escolheu por
  // último; senão a Sede — tudo nasce vinculado a ela; senão a primeira da
  // conta. Sempre uma unidade que a conta acessa: se a guardada não é (ou
  // deixou de ser), troca — mas só com o acesso de verdade na mão.
  useEffect(() => {
    if (accessibleIds.length === 0) return;
    if (currentUnitId && (accessibleIds.includes(currentUnitId) || !accessReady)) return;
    let stored: string | null = null;
    try { stored = localStorage.getItem(UNIT_STORAGE_KEY); } catch { /* sem storage */ }
    const storedOk = !!stored && accessibleIds.includes(stored);
    // Lista ainda do cache (pode ter só parte das unidades) ou dono ainda
    // não conferido: não descarta a unidade que o computador guardou.
    if (!currentUnitId && stored && !storedOk && !accessReady) return;
    setCurrentUnitIdRaw(storedOk && stored
      ? stored
      : accessibleIds.includes(SEDE_ID) ? SEDE_ID : accessibleIds[0]);
  }, [accessibleIds, accessReady, currentUnitId]);

  return (
    <ClinicContext.Provider value={{
      clinicId, currentUnitId, units, accessibleUnits, allUnitsAccess, isOwner,
      setClinicId, setCurrentUnitId, allUnitsView, setAllUnitsView,
    }}>
      {children}
    </ClinicContext.Provider>
  );
}
